import { createContext, useCallback, useContext, useEffect, useRef, useState, type ReactNode } from 'react';
import type {
  RecoveryContactSummary, InboundRecoveryRelationship,
  RecoveryAuditEntry, DesignateRecoveryContactRequest, ApproveResetRequest,
  RecoveryResetPackageResponse, SyncPullResponse,
  PendingVerification,
} from '@heybesafe/shared';
import * as api from '../api/client';
import { useSafe, type VaultStatus } from './SafeContext';

interface RecoveryContextValue {
  outbound: RecoveryContactSummary[];
  inbound: InboundRecoveryRelationship[];
  /** Designations in progress: someone is at the verify step and waiting to
   *  compare words right now. The designation itself does not exist yet. */
  pendingVerifications: PendingVerification[];
  audit: RecoveryAuditEntry[];
  loading: boolean;
  refresh: () => Promise<void>;
  designate: (req: DesignateRecoveryContactRequest) => Promise<void>;
  removeContact: (contactUserId: string) => Promise<void>;
  acceptDesignation: (protectedUserId: string) => Promise<void>;
  declineDesignation: (protectedUserId: string) => Promise<void>;
  requestEmergencyAccess: (protectedUserId: string) => Promise<void>;
  cancelEmergencyAccess: (contactUserId: string) => Promise<void>;
  cancelReset: (contactUserId: string) => Promise<void>;
  approveReset: (protectedUserId: string, body: ApproveResetRequest) => Promise<void>;
  denyReset: (protectedUserId: string) => Promise<void>;
  fetchWrappedVaultKey: (protectedUserId: string) => Promise<string>;
  fetchBorrowedItems: (protectedUserId: string, since?: string) => Promise<SyncPullResponse>;
  fetchResetPackage: (protectedUserId: string) => Promise<RecoveryResetPackageResponse>;
}

/** Minimum gap between focus-triggered refreshes. Long enough that tab-hopping
 *  doesn't hammer three endpoints, short enough that a designation someone
 *  creates while you're signed in shows up without a reload. */
const REFRESH_THROTTLE_MS = 60_000;

const RecoveryContext = createContext<RecoveryContextValue | null>(null);

/**
 * Non-throwing variant: returns null when no <RecoveryProvider> is mounted.
 * Used by onboarding, which lives inside the provider in the real app but may
 * be rendered (as part of SafePage) without it in isolated tests.
 */
export function useRecoveryOptional(): RecoveryContextValue | null {
  return useContext(RecoveryContext);
}

export function useRecovery(): RecoveryContextValue {
  const v = useContext(RecoveryContext);
  if (!v) throw new Error('useRecovery must be used inside <RecoveryProvider>');
  return v;
}

export function RecoveryProvider({ children }: { children: ReactNode }): JSX.Element {
  const { status } = useSafe();
  const [outbound, setOutbound] = useState<RecoveryContactSummary[]>([]);
  const [inbound, setInbound] = useState<InboundRecoveryRelationship[]>([]);
  const [pendingVerifications, setPendingVerifications] = useState<PendingVerification[]>([]);
  const [audit, setAudit] = useState<RecoveryAuditEntry[]>([]);
  // Initial true so pages that gate on `loading` (e.g. RecoveryReadOnlySafePage)
  // wait for the first unlock-triggered fetch before deciding to redirect.
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    setLoading(true);
    const [outR, inR, auR] = await Promise.all([
      api.listRecoveryContacts(),
      api.listInboundRecovery(),
      api.getRecoveryAudit(),
    ]);
    if (outR.ok) setOutbound(outR.data.contacts);
    if (inR.ok) {
      setInbound(inR.data.relationships);
      // Absent on an older server — treat as empty, not as an error.
      setPendingVerifications(inR.data.pendingVerifications ?? []);
    }
    if (auR.ok) setAudit(auR.data.entries);
    setLoading(false);
  }, []);

  // Refresh once each time the safe transitions to 'unlocked' (and once on
  // mount if it's already unlocked).
  const prevStatus = useRef<VaultStatus | undefined>(undefined);
  useEffect(() => {
    const prev = prevStatus.current;
    prevStatus.current = status;
    if (status === 'unlocked' && prev !== 'unlocked') {
      void refresh();
      lastRefreshRef.current = Date.now();
    }
  }, [status, refresh]);

  // …and again when the tab regains focus, throttled.
  //
  // Unlock-only was not enough: everything in here changes because SOMEONE ELSE
  // acted, so a user who is already signed in when a designation arrives never
  // sees it — the shell banner reads provider state, and nothing refetched it
  // until the next lock/unlock. An earlier version refreshed on every focus and
  // was reverted as too chatty (three endpoints per tab switch); the throttle is
  // what makes it affordable.
  const lastRefreshRef = useRef<number>(0);
  useEffect(() => {
    const onFocus = () => {
      if (status !== 'unlocked') return;
      if (Date.now() - lastRefreshRef.current < REFRESH_THROTTLE_MS) return;
      lastRefreshRef.current = Date.now();
      void refresh();
    };
    window.addEventListener('focus', onFocus);
    return () => { window.removeEventListener('focus', onFocus); };
  }, [status, refresh]);

  const designate = useCallback(async (req: DesignateRecoveryContactRequest) => {
    const r = await api.designateRecoveryContact(req);
    if (!r.ok) throw new Error(r.error);
    await refresh();
  }, [refresh]);

  const removeContact = useCallback(async (contactUserId: string) => {
    const r = await api.removeRecoveryContact(contactUserId);
    if (!r.ok) throw new Error(r.error);
    await refresh();
  }, [refresh]);

  const acceptDesignation = useCallback(async (protectedUserId: string) => {
    const r = await api.acceptDesignation(protectedUserId);
    if (!r.ok) throw new Error(r.error);
    await refresh();
  }, [refresh]);

  const declineDesignation = useCallback(async (protectedUserId: string) => {
    const r = await api.declineDesignation(protectedUserId);
    if (!r.ok) throw new Error(r.error);
    await refresh();
  }, [refresh]);

  const requestEmergencyAccess = useCallback(async (protectedUserId: string) => {
    const r = await api.requestEmergencyAccess(protectedUserId);
    if (!r.ok) throw new Error(r.error);
    await refresh();
  }, [refresh]);

  const cancelEmergencyAccess = useCallback(async (contactUserId: string) => {
    const r = await api.cancelEmergencyAccess(contactUserId);
    if (!r.ok) throw new Error(r.error);
    await refresh();
  }, [refresh]);

  const cancelReset = useCallback(async (contactUserId: string) => {
    const r = await api.cancelRecoveryReset(contactUserId);
    if (!r.ok) throw new Error(r.error);
    await refresh();
  }, [refresh]);

  const approveReset = useCallback(async (protectedUserId: string, body: ApproveResetRequest) => {
    const r = await api.approveReset(protectedUserId, body);
    if (!r.ok) throw new Error(r.error);
    await refresh();
  }, [refresh]);

  const denyReset = useCallback(async (protectedUserId: string) => {
    const r = await api.denyReset(protectedUserId);
    if (!r.ok) throw new Error(r.error);
    await refresh();
  }, [refresh]);

  const fetchWrappedVaultKey = useCallback(async (protectedUserId: string): Promise<string> => {
    const r = await api.getWrappedVaultKey(protectedUserId);
    if (!r.ok) throw new Error(r.error);
    return r.data.wrappedVaultKey;
  }, []);

  const fetchBorrowedItems = useCallback(async (protectedUserId: string, since?: string): Promise<SyncPullResponse> => {
    const r = await api.getRecoveryItems(protectedUserId, since);
    if (!r.ok) throw new Error(r.error);
    return r.data;
  }, []);

  const fetchResetPackage = useCallback(async (protectedUserId: string): Promise<RecoveryResetPackageResponse> => {
    const r = await api.getResetPackage(protectedUserId);
    if (!r.ok) throw new Error(r.error);
    return r.data;
  }, []);

  return (
    <RecoveryContext.Provider value={{
      outbound, inbound, pendingVerifications, audit, loading, refresh,
      designate, removeContact, acceptDesignation, declineDesignation,
      requestEmergencyAccess, cancelEmergencyAccess, cancelReset,
      approveReset, denyReset, fetchWrappedVaultKey, fetchBorrowedItems, fetchResetPackage,
    }}>
      {children}
    </RecoveryContext.Provider>
  );
}
